/**
 * MenuScene - Apple-style Glassmorphism UI
 * Modern, responsive design with settings panel
 */

import Phaser from 'phaser';
import LEVELS from '../configs/LevelConfigs';
import { GlassPanel, GlassButton, ModernText, ProgressBar } from '../ui/components';
import { UIColors, UITypography, UIAnimation, UISpacing, hexToString } from '../ui/UIDesignTokens';
import serviceLocator, { Services } from '../core/ServiceLocator';

export default class MenuScene extends Phaser.Scene {
    constructor() {
        super('MenuScene');
        this.settings = {
            soundEnabled: true,
            musicEnabled: true,
            vibration: true,
            showTimer: true,
            showControls: true,
            targetFPS: 60,  // 30, 60, 120, 0 (unlimited)
            vsyncEnabled: true
        };
        // Available FPS options
        this.fpsOptions = [30, 60, 120, 0]; // 0 = unlimited
    }

    create() {
        const { width, height } = this.cameras.main;
        
        // Load saved settings
        this.loadSettings();
        
        // Animated gradient background
        this.createBackground(width, height);
        
        // Floating particles
        this.createFloatingParticles(width, height);
        
        // Animated ball mascot
        this.createBallMascot(width, height);
        
        // Title with modern typography
        this.createTitle(width, height);
        
        // Main menu buttons
        this.createMenuButtons(width, height);
        
        // Footer
        this.createFooter(width, height);
        
        // Fade in
        this.cameras.main.fadeIn(600, 0, 0, 0);
    }

    loadSettings() {
        try {
            const saved = localStorage.getItem('bigblueball_settings');
            if (saved) {
                this.settings = { ...this.settings, ...JSON.parse(saved) };
            }
        } catch (e) {
            console.log('Could not load settings');
        }
    }

    saveSettings() {
        try {
            localStorage.setItem('bigblueball_settings', JSON.stringify(this.settings));
        } catch (e) {
            console.log('Could not save settings');
        }
    }

    createBackground(width, height) {
        const graphics = this.add.graphics();
        
        // Modern dark gradient
        const colors = [
            { pos: 0, color: 0x0a0a0f },
            { pos: 0.3, color: 0x0d1b2a },
            { pos: 0.6, color: 0x1b263b },
            { pos: 1, color: 0x0d1b2a }
        ];
        
        const steps = 60;
        for (let i = 0; i < steps; i++) {
            const t = i / steps;
            let color;
            
            if (t < 0.3) {
                color = this.lerpColor(colors[0].color, colors[1].color, t / 0.3);
            } else if (t < 0.6) {
                color = this.lerpColor(colors[1].color, colors[2].color, (t - 0.3) / 0.3);
            } else {
                color = this.lerpColor(colors[2].color, colors[3].color, (t - 0.6) / 0.4);
            }
            
            graphics.fillStyle(color, 1);
            graphics.fillRect(0, (height / steps) * i, width, height / steps + 2);
        }
        
        this.createMeshGradient(width, height);
    }

    createMeshGradient(width, height) {
        const blobs = [
            { x: width * 0.2, y: height * 0.3, radius: 300, color: 0x007AFF, alpha: 0.08 },
            { x: width * 0.8, y: height * 0.7, radius: 350, color: 0x5856D6, alpha: 0.06 },
            { x: width * 0.5, y: height * 0.5, radius: 400, color: 0x0A84FF, alpha: 0.04 }
        ];
        
        blobs.forEach(blob => {
            const gradient = this.add.graphics();
            
            for (let i = 20; i > 0; i--) {
                const alpha = blob.alpha * (i / 20);
                const radius = blob.radius * (1 - (20 - i) / 25);
                gradient.fillStyle(blob.color, alpha);
                gradient.fillCircle(blob.x, blob.y, radius);
            }
            
            this.tweens.add({
                targets: gradient,
                x: blob.x + Phaser.Math.Between(-30, 30),
                y: blob.y + Phaser.Math.Between(-30, 30),
                duration: Phaser.Math.Between(8000, 12000),
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
        });
    }

    createFloatingParticles(width, height) {
        for (let i = 0; i < 25; i++) {
            const x = Phaser.Math.Between(0, width);
            const y = Phaser.Math.Between(0, height);
            const size = Phaser.Math.Between(2, 4);
            const alpha = Phaser.Math.FloatBetween(0.1, 0.25);
            
            const particle = this.add.circle(x, y, size, 0xffffff, alpha);
            
            this.tweens.add({
                targets: particle,
                y: y - Phaser.Math.Between(80, 180),
                x: x + Phaser.Math.Between(-30, 30),
                alpha: 0,
                duration: Phaser.Math.Between(6000, 10000),
                repeat: -1,
                onRepeat: () => {
                    particle.x = Phaser.Math.Between(0, width);
                    particle.y = height + 30;
                    particle.alpha = alpha;
                }
            });
        }
    }

    createBallMascot(width, height) {
        const ballY = height / 2 - 50;
        
        // Ball shadow
        const shadow = this.add.ellipse(width / 2, ballY + 65, 55, 14, 0x000000, 0.25);
        
        // Main ball
        const ball = this.add.circle(width / 2, ballY, 45, 0x007AFF);
        ball.setStrokeStyle(3, 0x5AC8FA);
        
        // Ball highlights
        const highlight = this.add.circle(width / 2 - 14, ballY - 14, 14, 0x66b3ff, 0.8);
        const highlight2 = this.add.circle(width / 2 + 8, ballY + 8, 5, 0x5AC8FA, 0.4);
        
        // Bouncing animation
        this.tweens.add({
            targets: [ball, highlight, highlight2],
            y: '-=18',
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
        
        this.tweens.add({
            targets: shadow,
            scaleX: 0.8,
            scaleY: 0.6,
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    createTitle(width, height) {
        const titleY = height / 2 - 150;
        
        // Title shadow
        this.add.text(width / 2 + 2, titleY + 2, 'BIG BLUE BALL', {
            fontSize: '52px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#000000',
            alpha: 0.3
        }).setOrigin(0.5);
        
        // Main title
        const title = this.add.text(width / 2, titleY, 'BIG BLUE BALL', {
            fontSize: '52px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5);
        
        title.setShadow(0, 0, '#007AFF', 20, true, true);
        
        // Subtitle
        this.add.text(width / 2, titleY + 48, 'A Physics Puzzle Platformer', {
            fontSize: '16px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#8E8E93'
        }).setOrigin(0.5);
    }

    createMenuButtons(width, height) {
        const buttonsY = height / 2 + 80;
        const buttonSpacing = 60;
        
        // Play button (primary)
        const playBtn = new GlassButton(this, width / 2, buttonsY, '▶  PLAY GAME', {
            width: 260,
            height: 54,
            bgColor: UIColors.primary,
            hoverColor: UIColors.primaryLight,
            fontSize: '18px',
            onClick: () => this.startGame()
        });
        playBtn.animateIn(200);
        
        // Level Select button
        const levelBtn = new GlassButton(this, width / 2, buttonsY + buttonSpacing, '📋  SELECT LEVEL', {
            width: 260,
            height: 54,
            bgColor: UIColors.gray800,
            bgAlpha: 0.7,
            hoverColor: UIColors.gray700,
            fontSize: '16px',
            onClick: () => this.showLevelSelect()
        });
        levelBtn.animateIn(350);
        
        // Settings button
        const settingsBtn = new GlassButton(this, width / 2, buttonsY + buttonSpacing * 2, '⚙️  SETTINGS', {
            width: 260,
            height: 54,
            bgColor: UIColors.gray800,
            bgAlpha: 0.5,
            hoverColor: UIColors.gray700,
            fontSize: '16px',
            onClick: () => this.showSettings()
        });
        settingsBtn.animateIn(500);
        
        this.menuButtons = [playBtn, levelBtn, settingsBtn];
    }

    createFooter(width, height) {
        this.add.text(width / 2, height - 25, '© 2026 Big Blue Ball  •  v2.0', {
            fontSize: '12px',
            fontFamily: UITypography.fontFamily,
            fill: '#48484A'
        }).setOrigin(0.5);
    }

    startGame() {
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.time.delayedCall(500, () => {
            this.scene.start('GameScene', { level: 1 });
        });
    }

    // ==================== LEVEL SELECT ====================

    showLevelSelect() {
        const { width, height } = this.cameras.main;
        
        this.menuButtons.forEach((btn, i) => {
            this.tweens.add({
                targets: btn.container,
                alpha: 0,
                x: width / 2 - 80,
                duration: 200,
                delay: i * 40
            });
        });
        
        this.time.delayedCall(280, () => {
            this.createLevelSelectPanel(width, height);
        });
    }

    createLevelSelectPanel(width, height) {
        // Store references for cleanup
        this.levelSelectElements = [];

        // Overlay
        const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0);
        overlay.setInteractive();
        this.levelSelectElements.push(overlay);
        
        this.tweens.add({
            targets: overlay,
            fillAlpha: 0.85,
            duration: 300
        });
        
        // Panel - taller to fit all 10 levels with scrolling
        const panelWidth = Math.min(520, width - 40);
        const panelHeight = Math.min(height - 40, 680);
        const panelX = width / 2 - panelWidth / 2;
        const panelY = (height - panelHeight) / 2;

        const panel = this.add.graphics();
        panel.fillStyle(0x1C1C1E, 0.95);
        panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 24);
        panel.lineStyle(1.5, 0xffffff, 0.1);
        panel.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 24);
        panel.setDepth(100);
        this.levelSelectElements.push(panel);
        
        // Title
        const selectTitle = this.add.text(width / 2, panelY + 35, 'SELECT LEVEL', {
            fontSize: '28px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5).setDepth(101);
        this.levelSelectElements.push(selectTitle);
        
        // Level cards with scrollable container
        const cardStartY = panelY + 90;  // More top padding
        const cardSpacing = 58;  // Compact spacing for 10 levels
        const maxVisibleCards = 7;  // Fewer visible to ensure first card is fully visible
        
        // Create mask for scrolling - adjusted position
        const maskShape = this.add.graphics();
        maskShape.fillStyle(0xffffff);
        maskShape.fillRect(panelX + 10, cardStartY - 30, panelWidth - 20, panelHeight - 160);
        maskShape.setVisible(false);
        this.levelSelectElements.push(maskShape);
        
        // Scrollable container
        this.levelCardsContainer = this.add.container(0, 0);
        this.levelCardsContainer.setDepth(101);
        this.levelCardsContainer.setMask(maskShape.createGeometryMask());
        this.levelSelectElements.push(this.levelCardsContainer);
        
        // Create all level cards
        this.scrollY = 0;
        this.maxScrollY = Math.max(0, (LEVELS.length - maxVisibleCards) * cardSpacing);
        
        LEVELS.forEach((level, i) => {
            const cardY = cardStartY + i * cardSpacing;
            this.createLevelCard(width / 2, cardY, level, i, this.levelCardsContainer);
        });
        
        // Scroll indicators if needed
        if (LEVELS.length > maxVisibleCards) {
            const scrollHint = this.add.text(width / 2, panelY + panelHeight - 70, '↕ Scroll to see all levels', {
                fontSize: '12px',
                fontFamily: UITypography.fontFamily,
                fill: '#8E8E93'
            }).setOrigin(0.5).setDepth(101);
            this.levelSelectElements.push(scrollHint);
            
            // Mouse wheel scrolling
            this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY) => {
                if (this.levelCardsContainer) {
                    this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScrollY);
                    this.levelCardsContainer.y = -this.scrollY;
                }
            });
        }
        
        // Back button
        const backBtn = new GlassButton(this, width / 2, panelY + panelHeight - 40, '← BACK', {
            width: 180,
            height: 44,
            bgColor: UIColors.gray700,
            bgAlpha: 0.6,
            fontSize: '15px',
            onClick: () => this.closeLevelSelect()
        });
        backBtn.setDepth(101);
        backBtn.animateIn(400);
        this.levelSelectElements.push(backBtn);
    }

    createLevelCard(x, y, level, index, container = null) {
        const cardWidth = Math.min(420, this.cameras.main.width - 100);
        const cardHeight = 50;  // Compacto para caber 10 niveles
        
        const themeColor = level.theme?.platformColor || UIColors.gray700;
        
        const card = this.add.graphics();
        card.fillStyle(themeColor, 0.35);
        card.fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 10);
        card.lineStyle(1, 0xffffff, 0.12);
        card.strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 10);
        card.setDepth(101);
        if (container) container.add(card);
        this.levelSelectElements.push(card);
        
        // Level number circle
        const numBg = this.add.circle(x - cardWidth/2 + 32, y, 16, UIColors.primary, 0.9);
        numBg.setDepth(101);
        if (container) container.add(numBg);
        this.levelSelectElements.push(numBg);
        
        const levelNum = this.add.text(x - cardWidth/2 + 32, y, `${index + 1}`, {
            fontSize: '14px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5).setDepth(102);
        if (container) container.add(levelNum);
        this.levelSelectElements.push(levelNum);
        
        // Level name
        const levelName = this.add.text(x - cardWidth/2 + 58, y - 6, level.name.toUpperCase(), {
            fontSize: '14px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightSemibold,
            fill: '#ffffff'
        }).setOrigin(0, 0.5).setDepth(102);
        if (container) container.add(levelName);
        this.levelSelectElements.push(levelName);
        
        // Subtitle
        const subtitle = this.add.text(x - cardWidth/2 + 58, y + 10, level.subtitle, {
            fontSize: '11px',
            fontFamily: UITypography.fontFamily,
            fill: '#AEAEB2'
        }).setOrigin(0, 0.5).setDepth(102);
        if (container) container.add(subtitle);
        this.levelSelectElements.push(subtitle);
        
        // Star count
        const starCount = level.stars?.length || 0;
        const starText = this.add.text(x + cardWidth/2 - 45, y, `⭐ ${starCount}`, {
            fontSize: '15px',
            fill: '#FFD700'
        }).setOrigin(0.5).setDepth(102);
        if (container) container.add(starText);
        this.levelSelectElements.push(starText);
        
        // Hit area
        const hitArea = this.add.rectangle(x, y, cardWidth, cardHeight, 0x000000, 0);
        hitArea.setInteractive({ useHandCursor: true });
        hitArea.setDepth(103);
        if (container) container.add(hitArea);
        this.levelSelectElements.push(hitArea);
        
        hitArea.on('pointerover', () => {
            card.clear();
            card.fillStyle(themeColor, 0.55);
            card.fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
            card.lineStyle(1.5, 0xffffff, 0.25);
            card.strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
        });
        
        hitArea.on('pointerout', () => {
            card.clear();
            card.fillStyle(themeColor, 0.35);
            card.fillRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
            card.lineStyle(1, 0xffffff, 0.12);
            card.strokeRoundedRect(x - cardWidth/2, y - cardHeight/2, cardWidth, cardHeight, 12);
        });
        
        hitArea.on('pointerdown', () => {
            this.cameras.main.fadeOut(400, 0, 0, 0);
            this.time.delayedCall(400, () => {
                this.scene.start('GameScene', { level: level.id });
            });
        });
    }

    closeLevelSelect() {
        // Remove wheel listener first
        this.input.off('wheel');
        
        // Clear the container reference
        if (this.levelCardsContainer) {
            this.levelCardsContainer.setMask(null);
        }
        this.levelCardsContainer = null;
        this.scrollY = 0;
        
        // Destroy all elements safely
        this.levelSelectElements.forEach(el => {
            if (el && el.destroy) {
                try {
                    el.destroy();
                } catch (e) {
                    // Element might already be destroyed
                }
            }
        });
        this.levelSelectElements = [];
        
        // Show menu buttons again
        if (this.menuButtons && this.menuButtons.length > 0) {
            this.menuButtons.forEach((btn, i) => {
                if (btn && btn.container) {
                    btn.container.x = this.cameras.main.width / 2;
                    btn.container.alpha = 0;
                    this.tweens.add({
                        targets: btn.container,
                        alpha: 1,
                        duration: 200,
                        delay: i * 50
                    });
                }
            });
        }
    }

    // ==================== SETTINGS PANEL ====================

    showSettings() {
        const { width, height } = this.cameras.main;
        
        this.menuButtons.forEach((btn, i) => {
            this.tweens.add({
                targets: btn.container,
                alpha: 0,
                x: width / 2 - 80,
                duration: 200,
                delay: i * 40
            });
        });
        
        this.time.delayedCall(280, () => {
            this.createSettingsPanel(width, height);
        });
    }

    createSettingsPanel(width, height) {
        this.settingsElements = [];

        // Overlay
        const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0);
        overlay.setInteractive();
        this.settingsElements.push(overlay);
        
        this.tweens.add({
            targets: overlay,
            fillAlpha: 0.85,
            duration: 300
        });
        
        // Panel - taller to fit more options
        const panelWidth = Math.min(450, width - 40);
        const panelHeight = 580;
        const panelX = width / 2 - panelWidth / 2;
        const panelY = height / 2 - panelHeight / 2;

        const panel = this.add.graphics();
        panel.fillStyle(0x1C1C1E, 0.95);
        panel.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 24);
        panel.lineStyle(1.5, 0xffffff, 0.1);
        panel.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 24);
        panel.setDepth(100);
        this.settingsElements.push(panel);
        
        // Title
        const title = this.add.text(width / 2, panelY + 35, '⚙️  SETTINGS', {
            fontSize: '24px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5).setDepth(101);
        this.settingsElements.push(title);
        
        // Section: Audio
        const audioSectionY = panelY + 75;
        this.createSectionHeader(width / 2, audioSectionY, 'AUDIO', panelWidth);
        
        const settingSpacing = 52;
        const audioStartY = audioSectionY + 35;
        
        // Sound Effects
        this.createSettingToggle(width / 2, audioStartY, 'Sound Effects', '🔊', 'soundEnabled');
        
        // Music
        this.createSettingToggle(width / 2, audioStartY + settingSpacing, 'Background Music', '🎵', 'musicEnabled');
        
        // Section: Performance
        const perfSectionY = audioStartY + settingSpacing * 2 + 15;
        this.createSectionHeader(width / 2, perfSectionY, 'PERFORMANCE', panelWidth);
        
        const perfStartY = perfSectionY + 35;
        
        // FPS Selector
        this.createFPSSelector(width / 2, perfStartY, panelWidth);
        
        // V-Sync
        this.createSettingToggle(width / 2, perfStartY + settingSpacing, 'V-Sync', '🖥️', 'vsyncEnabled');
        
        // Section: Gameplay
        const gameplaySectionY = perfStartY + settingSpacing * 2 + 15;
        this.createSectionHeader(width / 2, gameplaySectionY, 'GAMEPLAY', panelWidth);
        
        const gameplayStartY = gameplaySectionY + 35;
        
        // Show Timer
        this.createSettingToggle(width / 2, gameplayStartY, 'Show Timer', '⏱', 'showTimer');
        
        // Show Controls
        this.createSettingToggle(width / 2, gameplayStartY + settingSpacing, 'Control Hints', '🎮', 'showControls');

        // Back button
        const backBtn = new GlassButton(this, width / 2, panelY + panelHeight - 40, '← SAVE & BACK', {
            width: 170,
            height: 40,
            bgColor: UIColors.primary,
            bgAlpha: 0.8,
            fontSize: '14px',
            onClick: () => this.closeSettings()
        });
        backBtn.setDepth(101);
        backBtn.animateIn(400);
        this.settingsElements.push(backBtn);
    }

    createSectionHeader(x, y, text, panelWidth) {
        const rowWidth = Math.min(380, panelWidth - 40);
        const rowX = x - rowWidth / 2;
        
        const header = this.add.text(rowX, y, text, {
            fontSize: '11px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightSemibold,
            fill: '#8E8E93',
            letterSpacing: 1.5
        }).setOrigin(0, 0.5).setDepth(102);
        this.settingsElements.push(header);
    }

    createFPSSelector(x, y, panelWidth) {
        const rowWidth = Math.min(380, panelWidth - 40);
        const rowX = x - rowWidth / 2;

        // Row background
        const row = this.add.graphics();
        row.fillStyle(0x2C2C2E, 0.6);
        row.fillRoundedRect(rowX, y - 22, rowWidth, 44, 10);
        row.setDepth(101);
        this.settingsElements.push(row);
        
        // Icon
        const iconText = this.add.text(rowX + 16, y, '🎯', {
            fontSize: '18px'
        }).setOrigin(0, 0.5).setDepth(102);
        this.settingsElements.push(iconText);
        
        // Label
        const labelText = this.add.text(rowX + 45, y, 'Target FPS', {
            fontSize: '15px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#ffffff'
        }).setOrigin(0, 0.5).setDepth(102);
        this.settingsElements.push(labelText);
        
        // FPS buttons
        const buttonWidth = 48;
        const buttonSpacing = 6;
        const buttonsStartX = rowX + rowWidth - (buttonWidth * 4 + buttonSpacing * 3) - 12;
        
        this.fpsOptions.forEach((fps, i) => {
            const btnX = buttonsStartX + i * (buttonWidth + buttonSpacing);
            const isSelected = this.settings.targetFPS === fps;
            const label = fps === 0 ? '∞' : fps.toString();
            
            const btn = this.add.graphics();
            btn.fillStyle(isSelected ? 0x007AFF : 0x3A3A3C, 1);
            btn.fillRoundedRect(btnX, y - 14, buttonWidth, 28, 6);
            btn.setDepth(102);
            this.settingsElements.push(btn);
            
            // Store reference for updating
            btn.fpsValue = fps;
            btn.isSelected = isSelected;
            
            const text = this.add.text(btnX + buttonWidth / 2, y, label, {
                fontSize: '13px',
                fontFamily: UITypography.fontFamilyMono,
                fontStyle: UITypography.weightBold,
                fill: isSelected ? '#ffffff' : '#AEAEB2'
            }).setOrigin(0.5).setDepth(103);
            this.settingsElements.push(text);
            
            // Hit area
            const hitArea = this.add.rectangle(btnX + buttonWidth / 2, y, buttonWidth, 28, 0x000000, 0);
            hitArea.setInteractive({ useHandCursor: true });
            hitArea.setDepth(104);
            this.settingsElements.push(hitArea);
            
            hitArea.on('pointerdown', () => {
                this.settings.targetFPS = fps;
                this.applyFPSSetting(fps);
                // Refresh panel to update selection
                this.refreshSettingsPanel();
            });
        });
    }

    applyFPSSetting(fps) {
        // Apply FPS setting to Phaser game loop
        if (fps === 0) {
            // Unlimited - remove cap
            this.game.loop.targetFps = 1000;
        } else {
            this.game.loop.targetFps = fps;
        }
    }

    applyVSyncSetting(enabled) {
        // V-Sync is controlled via requestAnimationFrame (browser-level)
        // We can simulate by forcing setTimeout on the game loop
        if (!enabled) {
            this.game.loop.forceSetTimeOut = true;
        } else {
            this.game.loop.forceSetTimeOut = false;
        }
    }

    refreshSettingsPanel() {
        const { width, height } = this.cameras.main;
        
        // Destroy current elements
        this.settingsElements.forEach(el => {
            if (el.destroy) el.destroy();
        });
        this.settingsElements = [];
        
        // Recreate panel
        this.createSettingsPanel(width, height);
    }

    createSettingToggle(x, y, label, icon, settingKey) {
        const { width } = this.cameras.main;
        const rowWidth = Math.min(380, width - 80);
        const rowX = x - rowWidth / 2;

        // Row background
        const row = this.add.graphics();
        row.fillStyle(0x2C2C2E, 0.6);
        row.fillRoundedRect(rowX, y - 22, rowWidth, 44, 10);
        row.setDepth(101);
        this.settingsElements.push(row);
        
        // Icon
        const iconText = this.add.text(rowX + 16, y, icon, {
            fontSize: '18px'
        }).setOrigin(0, 0.5).setDepth(102);
        this.settingsElements.push(iconText);
        
        // Label
        const labelText = this.add.text(rowX + 45, y, label, {
            fontSize: '15px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#ffffff'
        }).setOrigin(0, 0.5).setDepth(102);
        this.settingsElements.push(labelText);
        
        // Toggle switch
        const toggleX = rowX + rowWidth - 40;
        const isOn = this.settings[settingKey];
        
        // Toggle background
        const toggleBg = this.add.graphics();
        this.drawToggle(toggleBg, toggleX, y, isOn);
        toggleBg.setDepth(102);
        this.settingsElements.push(toggleBg);
        
        // Toggle knob
        const knob = this.add.circle(isOn ? toggleX + 20 : toggleX - 20, y, 10, 0xffffff);
        knob.setDepth(103);
        this.settingsElements.push(knob);
        
        // Hit area
        const hitArea = this.add.rectangle(toggleX, y, 55, 35, 0x000000, 0);
        hitArea.setInteractive({ useHandCursor: true });
        hitArea.setDepth(104);
        this.settingsElements.push(hitArea);
        
        hitArea.on('pointerdown', () => {
            this.settings[settingKey] = !this.settings[settingKey];
            const newState = this.settings[settingKey];
            
            // Animate toggle
            this.tweens.add({
                targets: knob,
                x: newState ? toggleX + 20 : toggleX - 20,
                duration: 150,
                ease: 'Power2.easeOut'
            });
            
            // Redraw toggle background
            this.drawToggle(toggleBg, toggleX, y, newState);
            
            // Apply V-Sync setting immediately
            if (settingKey === 'vsyncEnabled') {
                this.applyVSyncSetting(newState);
            }
        });
    }

    drawToggle(graphics, x, y, isOn) {
        graphics.clear();
        graphics.fillStyle(isOn ? 0x34C759 : 0x3A3A3C, 1);
        graphics.fillRoundedRect(x - 24, y - 13, 48, 26, 13);
    }

    closeSettings() {
        // Save settings
        this.saveSettings();
        
        // Destroy elements
        this.settingsElements.forEach(el => {
            if (el.destroy) el.destroy();
        });
        this.settingsElements = [];
        
        // Show menu buttons
        this.menuButtons.forEach((btn, i) => {
            btn.container.x = this.cameras.main.width / 2;
            btn.container.alpha = 0;
            this.tweens.add({
                targets: btn.container,
                alpha: 1,
                duration: 200,
                delay: i * 50
            });
        });
    }

    lerpColor(color1, color2, t) {
        const r1 = (color1 >> 16) & 0xFF;
        const g1 = (color1 >> 8) & 0xFF;
        const b1 = color1 & 0xFF;
        const r2 = (color2 >> 16) & 0xFF;
        const g2 = (color2 >> 8) & 0xFF;
        const b2 = color2 & 0xFF;
        
        const r = Math.floor(r1 + t * (r2 - r1));
        const g = Math.floor(g1 + t * (g2 - g1));
        const b = Math.floor(b1 + t * (b2 - b1));
        
        return (r << 16) | (g << 8) | b;
    }
}
